// Phase 3.2-B — frontend API client for the spare-procurement bridge.
// Talks to /api/v1/spare-procurement/* endpoints built in Phase 3.2-A.

import apiClient from '@/shared/api/client'

export type LinkStatus =
  | 'AWAITING_APPROVAL'
  | 'AWAITING_PO'
  | 'AWAITING_GRN'
  | 'AWAITING_INVOICE'
  | 'COMPLETE'
  | 'CANCELLED'

export type InvoiceMatchStatus =
  | 'MATCHED' | 'VARIANCE_FOUND' | 'BLOCKED'
  | 'OVERRIDE_APPROVED' | 'POSTED'

export interface CheckAndRaisePRResult {
  decision: 'STOCK_OK' | 'PR_RAISED' | 'PR_EXISTS'
  pr_id: string | null
  pr_no?: string
  link_id: string | null
  shortage_qty: number
  projected_stock: number
  status: LinkStatus | null
}

export interface ProcurementStatusRow {
  id: string
  spare_id: string | null
  required_qty: number
  pr_id: string | null
  po_id: string | null
  grn_id: string | null
  pve_id: string | null
  status: LinkStatus
  direct_charge_to_work_order: boolean
  created_at: string | null
  updated_at: string | null
}

export interface PipelineSummary {
  AWAITING_APPROVAL: number
  AWAITING_PO: number
  AWAITING_GRN: number
  AWAITING_INVOICE: number
  COMPLETE: number
  CANCELLED: number
}

export interface EnrichedLinkRow {
  link_id: string
  status: LinkStatus
  direct_charge: boolean
  created_at: string | null
  updated_at: string | null
  work_order_id: string
  work_order_no: string
  wo_priority: string
  machine_id: string | null
  machine_code: string | null
  machine_name: string | null
  department_id: string | null
  department_name: string | null
  spare_id: string | null
  spare_code: string | null
  spare_name: string | null
  required_qty: number
  current_stock: number | null
  pr_id: string | null
  pr_no: string | null
  pr_status: string | null
  po_id: string | null
  po_no: string | null
  po_status: string | null
  supplier_id: string | null
  supplier_name: string | null
  grn_id: string | null
  grn_line_id: string | null
  grn_qty_accepted: number | null
  grn_landed_rate: number | null
  pve_id: string | null
  invoice_no: string | null
  invoice_total_payable: number | null
  match_status: InvoiceMatchStatus | null
}

export interface SpareGRNLineInput {
  po_line_id?: string
  spare_id?: string
  maintenance_work_order_id?: string
  direct_charge_to_work_order?: boolean
  quantity_received: number
  quantity_accepted?: number
  quantity_rejected?: number
  rate_per_unit?: number
  uom?: string
  gst_pct?: number
  hsn_code?: string
  qc_status?: 'PENDING' | 'APPROVED' | 'REJECTED'
  remarks?: string
}

export interface SpareGRNResult {
  grn_id: string
  grn_no: string
  total_value: number
  lines: Array<{
    id: string
    spare_id: string | null
    quantity_accepted: number
    quantity_rejected: number
    rate_per_unit: number
    landed_rate_per_unit: number
    total_value: number
  }>
}

export interface SpareInvoiceLineInput {
  grn_line_id: string
  invoice_qty: number
  invoice_rate: number
  invoice_gst_pct?: number
}

export interface SpareInvoiceResult {
  pve_id: string
  voucher_id: string | null
  match_status: InvoiceMatchStatus
  total_payable: number
  variance_summary?: {
    lines: Array<Record<string, unknown>>
    qty_variance_pct_max: number
    rate_variance_pct_max: number
  }
}

export interface PendingLiabilityRow {
  pve_id: string
  vendor_id: string | null
  vendor_name: string | null
  po_no: string | null
  work_order_id: string | null
  work_order_no: string | null
  machine_id: string | null
  department_id: string | null
  invoice_no: string
  invoice_date: string | null
  invoice_amount: number
  paid: number
  balance_payable: number
  due_date: string | null
  match_status: InvoiceMatchStatus
}

export interface DirectChargeRow {
  grn_line_id: string
  grn_no: string
  grn_date: string | null
  work_order_id: string
  work_order_no: string
  machine_id: string | null
  department_id: string | null
  spare_id: string | null
  spare_code: string | null
  spare_name: string | null
  supplier_name: string | null
  po_no: string | null
  invoice_no: string | null
  quantity_accepted: number
  uom: string
  landed_rate: number
  total_cost: number
}

export interface POAdvance {
  advance_id: string
  amount: number
  settled_amount: number
  remaining: number
  status: 'OPEN' | 'PARTIAL' | 'SETTLED' | 'WRITTEN_OFF'
  advance_date: string
}

// ── Service object ─────────────────────────────────────────────────────

export const spareProcurementApi = {
  checkAndRaisePR: async (body: {
    work_order_id: string
    spare_id: string
    required_qty: number
    direct_charge?: boolean
    required_by?: string
  }): Promise<CheckAndRaisePRResult> => {
    const r = await apiClient.post('/spare-procurement/check-and-raise-pr', body)
    return r.data
  },

  workOrderStatus: async (woId: string): Promise<ProcurementStatusRow[]> => {
    const r = await apiClient.get(`/spare-procurement/work-orders/${woId}/status`)
    return r.data
  },

  pipelineSummary: async (plant_id: string): Promise<PipelineSummary> => {
    const r = await apiClient.get('/spare-procurement/pipeline', { params: { plant_id } })
    return r.data
  },

  listLinks: async (params: {
    plant_id: string
    department_id?: string
    machine_id?: string
    wo_id?: string
    spare_id?: string
    status?: LinkStatus
    direct_charge_only?: boolean
    limit?: number
  }): Promise<EnrichedLinkRow[]> => {
    const r = await apiClient.get('/spare-procurement/links', { params })
    return r.data
  },

  createSpareGRN: async (body: {
    po_id: string
    lines: SpareGRNLineInput[]
    invoice_amount?: number
    freight_total?: number
    remarks?: string
  }): Promise<SpareGRNResult> => {
    const r = await apiClient.post('/spare-procurement/grn', body)
    return r.data
  },

  bookSpareInvoice: async (
    body: {
      po_id: string
      grn_id: string
      invoice_no: string
      invoice_date: string
      lines: SpareInvoiceLineInput[]
      qty_tol_pct?: number
      rate_tol_pct?: number
      override_reason?: string
    },
    applyOverride = false,
  ): Promise<SpareInvoiceResult> => {
    const r = await apiClient.post('/spare-procurement/invoice', body, {
      params: { apply_override: applyOverride },
    })
    return r.data
  },

  recordAdvance: async (body: {
    po_id: string
    amount: number
    advance_date: string
    expected_settlement_date?: string
    purpose?: string
  }): Promise<{
    advance_id: string
    po_id: string
    amount: number
    status: string
    advance_date: string
  }> => {
    const r = await apiClient.post('/spare-procurement/advance', body)
    return r.data
  },

  advancesForPO: async (poId: string): Promise<POAdvance[]> => {
    const r = await apiClient.get(`/spare-procurement/advance/po/${poId}`)
    return r.data
  },

  pendingLiability: async (params: {
    plant_id: string
    vendor_id?: string
    department_id?: string
    machine_id?: string
    limit?: number
  }): Promise<PendingLiabilityRow[]> => {
    const r = await apiClient.get('/spare-procurement/pending-liability', { params })
    return r.data
  },

  directCharge: async (params: {
    plant_id: string
    wo_id?: string
    limit?: number
  }): Promise<DirectChargeRow[]> => {
    const r = await apiClient.get('/spare-procurement/direct-charge', { params })
    return r.data
  },
}
